// src/components/ace/Spotlight.jsx
//
// Cursor-following spotlight effect — wraps any content and renders
// a soft radial light that tracks the mouse. Aceternity-style.
//
// Usage:
//   <Spotlight color="#00e5a0" size={420}>
//     <YourContent />
//   </Spotlight>
//
// The wrapper sets position:relative; the light layer is absolute and
// pointer-events:none so it never interferes with inner controls.
//
import React, { useRef } from 'react';

export default function Spotlight({
  children,
  color = '#00e5a0',
  size = 380,
  intensity = 0.18,
  className = '',
  style = {},
}) {
  const ref = useRef(null);
  const lightRef = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--spx', `${e.clientX - r.left}px`);
    el.style.setProperty('--spy', `${e.clientY - r.top}px`);
    if (lightRef.current) lightRef.current.style.opacity = '1';
  };

  const onLeave = () => {
    if (lightRef.current) lightRef.current.style.opacity = '0';
  };

  // Convert color to rgba w/ alpha for the gradient
  const rgba = (hex, a) => {
    const h = hex.replace('#', '');
    const n = h.length === 3
      ? h.split('').map(c => parseInt(c + c, 16))
      : [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16));
    return `rgba(${n[0]}, ${n[1]}, ${n[2]}, ${a})`;
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={className}
      style={{
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
    >
      <div
        ref={lightRef}
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `radial-gradient(${size}px circle at var(--spx, 50%) var(--spy, 50%), ${rgba(color, intensity)}, transparent 65%)`,
          opacity: 0,
          transition: 'opacity 0.25s ease',
          zIndex: 1,
        }}
      />
      <div style={{ position: 'relative', zIndex: 2 }}>{children}</div>
    </div>
  );
}
